import { useMemo } from 'react';
import { FileCheck, Hourglass, Check, Clock, Eye, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { TRIAGEM_TH } from '@/components/kanban/triagem/triagemTabela';
import { useTriagemCotacao, type ItemTriagemCotacao } from './useTriagemCotacao';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { TriagemCotacaoRow } from './TriagemCotacaoRow';
import { separarAparelho, useFlagsFaturamento } from '@/components/kanban/triagem/flagsFaturamento';

interface TriagemCotacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TriagemCotacaoModal({ isOpen, onClose }: TriagemCotacaoModalProps) {
  const {
    itens: itensTodos,
    isLoading,
    refetch,
    moverParaAnaliseManual,
    moverParaErroInput,
    moverParaInspecaoConcluida,
    moverParaFinal,
  } = useTriagemCotacao();
  /* Só a cotação clicada fica ocupada (as linhas dela andam juntas); o resto
     da fila continua livre (`triagem/acaoTriagem.ts`). */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const chaveDaLinha = (i: ItemTriagemCotacao) => i.numero_cotacao_operadora || i.item_id;

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );

  const uniquePedidos = new Set(itens.map(i => i.pedido_id)).size;
  // Aparelho anda SEPARADO do faturamento — tem KPI próprio e não é venda de
  // linha. Régua única em `flagsFaturamento.ts`.
  const flagsFat = useFlagsFaturamento();
  const { faturamento: totalValue, qtdAparelhos } = separarAparelho(
    itens, flagsFat, (i) => i.produto, (i) => (i.subtotal || 0),
  );

  return (
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={FileCheck}
      titulo="Triagem Cotação"
      contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: totalValue, aparelhos: qtdAparelhos }}
      segmento={segmento}
      onSegmento={setSegmento}
      consulta={{ chave: 'cotacao', pedidoIds: itens.map(i => i.pedido_id) }}
      sla={null}
      fifo={false}
      legenda={[
        { key: 'manter', Icone: Clock, label: 'Manter' },
        { key: 'an-lise', Icone: Hourglass, label: 'Análise' },
        { key: 'aprovado', Icone: Check, label: 'Aprovado' },
        { key: 'analisar', Icone: Eye, label: 'Analisar' },
        { key: 'cancelar', Icone: X, label: 'Cancelar' },
      ]}
      isLoading={isLoading}
      onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
      vazio={
        itens.length === 0
          ? { titulo: '\n                Nenhum item pendente\n              ', descricao: '\n                Todos os itens foram processados na triagem de cotação\n              ' }
          : undefined
      }
    >
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-10 bg-surface">
                <tr>
                  <th className={TRIAGEM_TH}>{rotuloSegmento(segmento)}</th>
                  <th className={TRIAGEM_TH}>Razão Social</th>
                  <th className={TRIAGEM_TH}>Cotação</th>
                  <th className={TRIAGEM_TH}>Pedido</th>
                  <th className={TRIAGEM_TH}>Produto</th>
                  <th className={TRIAGEM_TH}>QTD</th>
                  <th className={TRIAGEM_TH}>Valor</th>
                  <th className={TRIAGEM_TH}>Tempo</th>
                  <th className={`${TRIAGEM_TH} w-[172px]`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => (
                  <TriagemCotacaoRow
                    key={item.item_id}
                    item={item}
                    onMoverParaAnaliseManual={(itemId) => { void ocupar(chaveDaLinha(item), () => moverParaAnaliseManual(itemId)).catch(() => undefined); }}
                    onMoverParaErroInput={(itemId, textoOperadora) => ocupar(chaveDaLinha(item), () => moverParaErroInput({ itemId, textoOperadora }))}
                    onMoverParaInspecaoConcluida={(itemId) => { void ocupar(chaveDaLinha(item), () => moverParaInspecaoConcluida(itemId)).catch(() => undefined); }}
                    onMoverParaFinal={(itemId) => { void ocupar(chaveDaLinha(item), () => moverParaFinal(itemId)).catch(() => undefined); }}
                    onAfterCancel={() => { void refetch(); }}
                    ocupada={ocupada(chaveDaLinha(item))}
                  />
                ))}
              </tbody>
            </table>
    </TriagemShell>
  );
}
